import {
  ExpandMore,
  CheckBox,
  CheckBoxOutlineBlank,
  DriveFileRenameOutline
} from '@mui/icons-material';
// Components
import {
  Typography,
  FormLabel,
  FormControlLabel,
  Checkbox,
  FormControl,
  RadioGroup,
  Radio,
  TextField,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from '@mui/material';
import { ImageUpload } from '../../../components';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { FC, useEffect, useState, useRef, useMemo } from 'react';
import { IFieldWalkthroughDetailItem } from '../../../models';
import { formatCapitalCase } from '../../../helpers/format';

interface IMobileFieldWalkthroughItemProps {
  original: IFieldWalkthroughDetailItem;
  index: number;
  updateMyData: (d: IFieldWalkthroughDetailItem, columnId: string, value: any) => void;
  setCurrentImage: (val: string) => void;
  fieldWalkthroughId: string | number;
  handleImageUpload: any;
  isSavingImage: any;
  itemHeights: React.RefObject<any>;
  setItemHeights: (index: number, height: number) => void;
  resetItemHeights: (val: number) => void;
  expandedItems: {
    [id: number]: boolean;
  };
  setExpandedItems: (val: { [id: number]: boolean }) => void;
  selectedLocationId: number;
  isExpandAll?: boolean;
  selectedDeficientItems: IFieldWalkthroughDetailItem[];
  setSelectedDeficientItems: (val: IFieldWalkthroughDetailItem[]) => void;
  handleFlooplanMarkup: () => void;
}

export const MobileFieldWalkthroughItem: FC<IMobileFieldWalkthroughItemProps> = ({
  index,
  original,
  updateMyData,
  setCurrentImage,
  fieldWalkthroughId,
  handleImageUpload,
  isSavingImage,
  itemHeights,
  setItemHeights,
  resetItemHeights,
  expandedItems,
  setExpandedItems,
  selectedLocationId,
  isExpandAll,
  selectedDeficientItems,
  setSelectedDeficientItems,
  handleFlooplanMarkup
}) => {
  const classes = useStyles();
  const {
    text,
    fieldWalkthroughLocationId,
    foremanResponse,
    projectManagerResponse,
    supervisorResponse,
    notes,
    notApplicableMarked,
    fieldWalkthroughItemId,
    hasPhoto,
    isPunchlistItemComplete,
    assignedToDisplayName,
    hasPunchlistItem
  } = original;

  const isExpanded = useMemo(() => {
    return expandedItems[index] ?? isExpandAll ?? false;
  }, [expandedItems, index, isExpandAll]);

  const [internalNotes, setInternalNotes] = useState(notes ?? '');

  const itemRef = useRef<any>(null);
  useEffect(() => {
    if (itemRef.current) {
      setItemHeights(index, itemRef.current.getBoundingClientRect().height);
    }
  }, [setItemHeights, index, isExpanded]);

  const prevIsExpandedRef = useRef<any>(null);
  useEffect(() => {
    prevIsExpandedRef.current = isExpanded;
  }, [isExpanded]); // Update the ref every time isExpanded changes

  const prevIsExpanded = prevIsExpandedRef.current;
  useEffect(() => {
    if (prevIsExpanded !== isExpanded) {
      // Use setTimeout to delay the call to resetItemHeights
      setTimeout(() => resetItemHeights(index), 150);
    }
  }, [resetItemHeights, isExpanded, index, prevIsExpanded]);
  const setExpanded = (expanded: boolean) => {
    if (selectedLocationId === -1) {
      return setExpandedItems({ [index]: expanded });
    } else {
      return setExpandedItems({ ...expandedItems, [index]: expanded });
    }
  };
  return (
    <>
      <Accordion
        className={classes.root}
        expanded={isExpanded}
        onChange={(_, expanded) => {
          setExpanded(expanded);
        }}
        style={{ zIndex: isExpanded ? 10 : undefined }}
        key={`${isExpanded}`}
        ref={itemRef}
      >
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls={`${fieldWalkthroughLocationId}-content`}
          id={`${fieldWalkthroughLocationId}-header`}
        >
          <Box>
            <Typography variant="body1">{text}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          className={classes.details}
          id={`${fieldWalkthroughLocationId}-content`}
          aria-labelledby={`${fieldWalkthroughLocationId}-header`}
        >
          {hasPunchlistItem && !notApplicableMarked && (
            <Box alignItems="center">
              <Box display="flex" alignItems="center">
                <Typography variant="caption" sx={{ mr: 0.25, color: 'gray' }}>
                  Complete
                </Typography>
                {isPunchlistItemComplete ? (
                  <CheckBox sx={{ color: 'green', marginBottom: '4px', fontSize: '18px' }} />
                ) : (
                  <CheckBoxOutlineBlank
                    sx={{ color: 'gray', marginBottom: '4px', fontSize: '18px' }}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center">
                <Typography variant="caption" sx={{ mr: 0.25, color: 'gray' }}>
                  Assigned To:
                </Typography>
                <Typography variant="caption" sx={{ color: 'gray' }}>
                  {formatCapitalCase(assignedToDisplayName)}
                </Typography>
              </Box>
            </Box>
          )}
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              N/A:
            </FormLabel>
            <FormControlLabel
              color="primary"
              classes={{
                label: classes.checkboxLabel
              }}
              control={
                <Checkbox
                  checked={notApplicableMarked}
                  id="notApplicableMarked"
                  onChange={(_, checked) => {
                    updateMyData(original, 'notApplicableMarked', checked);
                    // remove the selected item if the checkbox is checked
                    setSelectedDeficientItems(
                      selectedDeficientItems.filter(
                        walkthroughItem =>
                          walkthroughItem.fieldWalkthroughItemId !== fieldWalkthroughItemId
                      )
                    );
                  }}
                />
              }
              label=""
            />
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Notes:
            </FormLabel>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              autoComplete="nope"
              value={internalNotes}
              classes={{
                root: classes.root
              }}
              onChange={e => setInternalNotes(e.target.value)}
              onBlur={() => {
                updateMyData(original, 'notes', internalNotes);
              }}
            />
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Photo:
            </FormLabel>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              {hasPhoto && (
                <picture
                  className={classes.pictureImages}
                  onClick={() =>
                    setCurrentImage(
                      `${window.location.origin}/api/field-walkthroughs/${fieldWalkthroughId}/items/${fieldWalkthroughItemId}/image`
                    )
                  }
                >
                  <img
                    className={classes.colorImage}
                    src={`${window.location.origin}/api/field-walkthroughs/${fieldWalkthroughId}/items/${fieldWalkthroughItemId}/image`}
                    alt=""
                  />
                </picture>
              )}
              <ImageUpload
                imageKey={`${index}`}
                showSource={false}
                disabled={isSavingImage[fieldWalkthroughItemId]}
                showLabel={false}
                handleFileChange={(_, file) => handleImageUpload(file, original, index)}
              />
              <IconButton
                color={original?.markupFiles?.length > 0 ? 'secondary' : 'primary'}
                size="large"
                onClick={() => {
                  handleFlooplanMarkup();
                }}
              >
                <DriveFileRenameOutline fontSize="large" />
              </IconButton>
            </Box>
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Foreman Response:
            </FormLabel>
            <FormControl fullWidth variant="outlined" size="small" required>
              <RadioGroup
                value={foremanResponse}
                onChange={(_, value) => updateMyData(original, 'foremanResponse', value)}
                aria-label="foremanResponse"
                name="foremanResponse"
              >
                <FormControlLabel
                  value="Meets Standards"
                  classes={{
                    label: classes.checkboxLabel
                  }}
                  control={<Radio />}
                  label="Meets Standards"
                  disabled={notApplicableMarked}
                />
                <FormControlLabel
                  value="Repair / Replace"
                  classes={{
                    label: classes.checkboxLabel
                  }}
                  control={<Radio />}
                  label="Repair / Replace"
                  disabled={notApplicableMarked}
                />
              </RadioGroup>
            </FormControl>
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Supervisor Response:
            </FormLabel>
            <FormControl fullWidth variant="outlined" size="small" required>
              <RadioGroup
                value={supervisorResponse}
                onChange={(_, value) => updateMyData(original, 'supervisorResponse', value)}
                aria-label="supervisorResponse"
                name="supervisorResponse"
              >
                <FormControlLabel
                  value="Meets Standards"
                  classes={{
                    label: classes.checkboxLabel
                  }}
                  control={<Radio />}
                  label="Meets Standards"
                  disabled={notApplicableMarked}
                />
                <FormControlLabel
                  value="Repair / Replace"
                  classes={{
                    label: classes.checkboxLabel
                  }}
                  control={<Radio />}
                  label="Repair / Replace"
                  disabled={notApplicableMarked}
                />
              </RadioGroup>
            </FormControl>
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              PM Response:
            </FormLabel>
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              required={
                foremanResponse === 'Meets Standards' || supervisorResponse === 'Meets Standards'
                  ? false
                  : true
              }
            >
              <RadioGroup
                value={projectManagerResponse}
                onChange={(_, value) => updateMyData(original, 'projectManagerResponse', value)}
                aria-label="projectManagerResponse"
                name="projectManagerResponse"
              >
                <FormControlLabel
                  value="Meets Standards"
                  classes={{
                    label: classes.checkboxLabel
                  }}
                  control={<Radio />}
                  label="Meets Standards"
                  disabled={notApplicableMarked}
                />
                <FormControlLabel
                  value="Repair / Replace"
                  classes={{
                    label: classes.checkboxLabel
                  }}
                  control={<Radio />}
                  label="Repair / Replace"
                  disabled={notApplicableMarked}
                />
              </RadioGroup>
            </FormControl>
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    fontSize: theme.typography.body2.fontSize,
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    marginBottom: '0.5rem',
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[50]
  },
  boldName: {
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    color: theme.palette.typography.labels,
    fontSize: theme.typography.body2.fontSize
  },
  pictureImages: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  colorImage: {
    width: 50,
    height: 50
  },
  checkboxLabel: {
    fontSize: theme.typography.body2.fontSize,
    position: 'relative',
    top: '.1rem'
  }
}));
